import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getBootstrap,
  login as loginService,
  rsminLogin as rsminLoginService,
  register as registerService,
  logoutApi,
} from '../services/authService';

const AuthContext = createContext(null);

// Empty session shape — mirrors the bootstrap response contract exactly.
// Every consumer can destructure with a stable shape regardless of load state.
const EMPTY_SESSION = {
  user:             null,
  panel:            null,   // 'admin' | 'staff' | 'proponent' | 'community' | 'user' | 'genpop'
  memberships:      [],
  activeTenant:     null,
  activeMembership: null,
  allowedModules:   {},
  access:           null,
  platformFeatures: { comsEnabled: false },
};

// Helpers to keep token storage in one place
const storeTokens = (token, refreshToken, userId) => {
  localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  if (userId)       localStorage.setItem('userId', String(userId));
};

const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
};

export const AuthProvider = ({ children }) => {
  const [session, setSession]   = useState(EMPTY_SESSION);
  const [loading, setLoading]   = useState(true);
  const [sessionError, setSessionError] = useState('');
  const sessionRequest = useRef(0);

  // Single function that fills the session from the backend bootstrap response.
  // Called on mount (if token present), after login, and after register.
  const loadSession = useCallback(async (throwOnError = false) => {
    const request = ++sessionRequest.current;
    try {
      const res = await getBootstrap();
      if (request !== sessionRequest.current) return;
      setSession(res.data);
      setSessionError('');
    } catch (error) {
      if (request !== sessionRequest.current) return;
      if ([401, 403].includes(error.response?.status)) {
        clearTokens();
        setSession(EMPTY_SESSION);
        setSessionError('');
      } else {
        if (throwOnError !== true) {
          setSessionError(error.response?.data?.message || 'Unable to reach CIP. Your sign-in has been kept. Retry when the server is available.');
        }
      }
      if (throwOnError === true) throw error;
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname === '/auth/rsmin-callback') {
      setLoading(false);
      return;
    }
    if (localStorage.getItem('token')) {
      loadSession().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadSession]);

  const login = useCallback(async (email, password) => {
    const res = await loginService({ email, password });
    storeTokens(res.data.token, res.data.refreshToken, res.data.user?._id);
    await loadSession();
  }, [loadSession]);

  const register = useCallback(async (data) => {
    const res = await registerService(data);
    storeTokens(res.data.token, res.data.refreshToken, res.data.user?._id);
    await loadSession();
  }, [loadSession]);

  const loginWithRSMinToken = useCallback(async (rsminToken) => {
    sessionRequest.current += 1;
    setSessionError('');
    const res = await rsminLoginService(rsminToken);
    storeTokens(res.data.token, res.data.refreshToken, res.data.user?._id);
    await loadSession(true);
  }, [loadSession]);

  const logout = useCallback(async () => {
    sessionRequest.current += 1;
    const userId = localStorage.getItem('userId');
    try { await logoutApi(userId); } catch { /* server-side invalidation best-effort */ }
    clearTokens();
    setSessionError('');
    setSession(EMPTY_SESSION);
  }, []);

  // Convenience: check if a named module is enabled for the current user
  const isModuleEnabled = useCallback((moduleName) => {
    return !!(session.allowedModules && session.allowedModules[moduleName]);
  }, [session.allowedModules]);

  const value = {
    // Full bootstrap payload (destructure whatever you need)
    ...session,
    user: session.user ? { ...session.user, access: session.access } : null,
    // Auth state
    loading,
    // Actions
    login,
    loginWithRSMinToken,
    register,
    logout,
    loadSession,
    // Helpers
    isModuleEnabled,
  };

  return (
    <AuthContext.Provider value={value}>
      {sessionError ? <div role="alert"><p>{sessionError}</p><button type="button" onClick={loadSession}>Retry connection</button></div> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
